/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnInit} from '@angular/core';
import * as _ from 'lodash';

import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';
import template from './keyword-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {TargetingHelper} from '../targeting-helper';
import {KeywordSearchResource} from '../../../components/api/resources/resource/keyword-search';
import {KeywordProfileSearchResource} from '../../../components/api/resources/resource/keyword-profile-search';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {BrowserLocationService} from '../../angular-platform-providers';

@Component({
  standalone: false,
  selector: 'adn-keyword-targeting',
  template
})
export class KeywordTargetingComponent implements OnInit {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.keyword;
  @Input() underModel: any;
  @Input() runningModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  inputted: any = {};
  inputted2: any = {};
  inputs: any[] = [];
  templateState: any = {
    loaded: true,
    complexInput: false
  };
  list: any = {searchResults: [], results: [], totalHits: 0, totalCount: 0, pageStart: 0, pageEnd: 0};
  searchTerm = '';
  resultsInfo: any = {};
  showSentiment = false;
  isMarketplace = false;
  searchMeta: any = {
    pageSize: 100,
    currentPage: 1,
    mode: 'ARTICLES',
    keywordChoice: 'ABOVE',
    resolved: true,
    error: false as any
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      const summary = TargetingHelper.isComplex(this.model, ADN_TARGETING_TYPES.keyword.id)
        ? (TargetingHelper.getSizeOfTargetingTypes(this.model, ADN_TARGETING_TYPES.keyword.targets) + ' keywords with complex logic')
        : this.inputted.value;
      return {
        vm: _.cloneDeep(this.inputted),
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.keyword,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly keywordSearchResource: KeywordSearchResource,
    private readonly keywordProfileSearchResource: KeywordProfileSearchResource,
    @Inject(BrowserLocationService) private readonly location: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.isMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.showSentiment = this.isMarketplace &&
      (this.location.hostname === 'localhost' || this.localUserProfile.get('username') === 'adnuntius@adnuntius.com');

    this.initAll();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.keyword.widget, () => {
      if (!this.isMarketplace) {
        this.search(true, 'KEYWORDS');
      }
    }, copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  search(newSearch?: boolean, mode?: string): void {
    this.searchMeta.resolved = false;
    this.searchMeta.error = false;
    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }
    this.searchMeta.mode = mode || this.searchMeta.mode;

    if (this.searchMeta.mode === 'KEYWORDS') {
      const params: any = {
        q: this.searchTerm || '',
        pageSize: this.searchMeta.pageSize,
        page: this.searchMeta.currentPage - 1
      };
      if (!params.q) {
        params.orderBy = 'FREQUENCY';
        params.orderByDirection = 'DESCENDING';
      }
      this.keywordSearchResource.query(params).then((page: any) => {
        this.ngZone.run(() => {
          if (!this.templateState.complexInput) {
            const trimmedValues = TargetingHelper.trimValues(this);
            _.forEach(_.get(page, ['searchResults'], []), (a: any) => {
              a.selected = _.indexOf(trimmedValues.value, a.keyword) > -1;
            });
          }
          this.list = page;
          this.searchMeta.resolved = true;
          this.cdr.markForCheck();
        });
      });
      return;
    }

    const articleSearchParams: any = {model: []};
    if (this.searchMeta.keywordChoice === 'BELOW') {
      articleSearchParams.inputted = _.cloneDeep(this.inputted2);
    } else {
      articleSearchParams.inputted = _.cloneDeep(this.inputted);
    }
    this.resultsInfo = {};
    if (this.templateState.complexInput) {
      articleSearchParams.inputs = _.cloneDeep(this.inputs);
      TargetingHelper.assignComplexValuesToNewModel(articleSearchParams, 'keywords', 'notKeywords');
    } else {
      if (_.get(articleSearchParams, ['inputted', 'value'], '').length === 0 &&
        _.get(articleSearchParams, ['inputted', 'valueAll'], '').length === 0 &&
        _.get(articleSearchParams, ['inputted', 'valueNotAny'], '').length > 0) {
        articleSearchParams.inputted.value = articleSearchParams.inputted.valueNotAny;
        articleSearchParams.inputted.valueNotAny = '';
        this.resultsInfo.showingNots = true;
      }
      this.resultsInfo = _.merge(this.resultsInfo, TargetingHelper.assignAllTheValuesToPipedModel(articleSearchParams, ADN_TARGETING_TYPES.keyword.apiProp, 1000));
      if (_.get(articleSearchParams, ['inputted', 'value'], '').length === 0 &&
        _.get(articleSearchParams, ['inputted', 'valueAll'], '').length === 0 &&
        _.get(articleSearchParams, ['inputted', 'valueNotAny'], '').length === 0) {
        deferMarkForCheck(this.cdr, () => {
          this.list = {results: []};
          this.searchMeta.error = 'Specify keywords to see what articles currently match';
          this.searchMeta.resolved = true;
        });
        return;
      }
    }
    const targeting = _.cloneDeep(this.runningModel);
    const keywordTarget = articleSearchParams.model[0];
    if (!keywordTarget) {
      deferMarkForCheck(this.cdr, () => {
        this.list = {results: []};
        this.searchMeta.error = 'Specify keywords to see what articles currently match';
        this.searchMeta.resolved = true;
      });
      return;
    }
    targeting.keywordTargets = [keywordTarget];
    this.keywordProfileSearchResource.post(targeting, this.searchMeta).then((page: any) => {
      this.ngZone.run(() => {
        _.forEach(page.results, (profile: any) => {
          profile.count = 0;
          profile.matched = profile.matched || [];
          profile.count = profile.matched.length * 100 + profile.keywords.length;
          profile.site = profile.url.split('/')[0];
          profile.title = profile.title || profile.url;
          profile.image = profile.image || '';
          _.remove(profile.keywords, (keyword: string) => profile.matched.includes(keyword));
          profile.all = profile.matched.concat(profile.keywords);
        });
        this.list = page;
        this.searchMeta.resolved = true;
        this.cdr.markForCheck();
      });
    });
  }

  changeKeywordPage(page: number): void {
    if (page < 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.search();
  }

  refine(): void {
    if (this.searchMeta.keywordChoice === 'BELOW') {
      this.inputted2 = _.cloneDeep(this.inputted);
    }
  }

  manageKeyword(keyword: string, type: string, remove?: boolean, index?: number): void {
    let param = 'value';
    if (type === 'ALL') {
      param = 'valueAll';
    } else if (type === 'NOT') {
      param = 'valueNotAny';
    } else if (type === 'ANY_AND') {
      param = 'valueAnded';
    }
    const inputtedElement = _.isNumber(index) ? this.inputted[param][index] : this.inputted[param];
    const arr = _.filter(_.map((_.get(inputtedElement, ['value'], inputtedElement) || '').split(','), (entry: string) => {
      return _.trim(entry);
    }), (entry: string) => {
      return entry && (!remove || entry !== keyword);
    });
    if (!remove) {
      arr.push(keyword);
    }
    if (_.isNumber(index)) {
      this.inputted[param][index].value = arr.join(', ');
    } else {
      this.inputted[param] = arr.join(', ');
    }
  }

  select(searchResult: any, param: string): void {
    searchResult.selected = true;
    const trimmedValues = TargetingHelper.trimValues(this);
    trimmedValues[param].push(searchResult.keyword);
    TargetingHelper.assignToInput(this, trimmedValues, undefined, undefined, undefined, undefined);
  }

  addOr(searchResult: any): void {
    this.addEntry(searchResult.keyword);
  }

  addAnd(searchResult: any): void {
    this.addRow(null, searchResult.keyword);
  }

  addAndNot(searchResult: any): void {
    this.addNotRow(null, searchResult.keyword);
  }

  addKeywordAnd(): void {
    this.inputted.valueAnded = this.inputted.valueAnded || [];
    this.inputted.valueAnded.push({value: ''});
  }

  addKeywordAnd2(): void {
    this.inputted2.valueAnded = this.inputted2.valueAnded || [];
    this.inputted2.valueAnded.push({value: ''});
  }

  addEntry(entry?: string): void {
    TargetingHelper.checkAndCreateBaseInput(this);
    const input = entry || '';
    this.inputs.push({keywords: [{k: input, not: false}], notKeywords: []});
  }

  addRow(index?: number | null, entry?: string): void {
    TargetingHelper.checkAndCreateBaseInput(this);
    const input = entry || '';
    const theIndex = _.isFinite(index) ? index as number : this.inputs.length - 1;
    this.inputs[theIndex].keywords.push({k: input, not: false});
  }

  addNotRow(index?: number | null, entry?: string): void {
    TargetingHelper.checkAndCreateBaseInput(this);
    const input = entry || '';
    const theIndex = _.isFinite(index) ? index as number : this.inputs.length - 1;
    this.inputs[theIndex].notKeywords.push({k: input, not: false});
  }

  negateRow(parentIndex: number, index: number, isNot?: boolean): void {
    const param = isNot ? 'notKeywords' : 'keywords';
    this.inputs[parentIndex][param][index].not = !this.inputs[parentIndex][param][index].not;
  }

  removeRow(parentIndex: number, index: number, isNot?: boolean): void {
    this.inputs[parentIndex][isNot ? 'notKeywords' : 'keywords'].splice(index, 1);
    if (this.inputs[parentIndex].keywords.length === 0 && this.inputs[parentIndex].notKeywords.length === 0) {
      this.inputs.splice(parentIndex, 1);
    }
  }

  doAdRequestSearch(): void {
    this.search(true, 'KEYWORDS');
  }

  private initAll(): void {
    this.templateState.complexInput = this.init();
  }

  private init(): boolean {
    TargetingHelper.complexInit(this);
    TargetingHelper.simpleInit(this, 'keywords');
    return !!TargetingHelper.isComplex(this.model, ADN_TARGETING_TYPES.keyword.id);
  }

  private prepareForSave(): void {
    if (!this.model) {
      return;
    }
    const matchingGroup = _.get(this.model, [0, 'matchingGroup'], 0);
    this.model.length = 0;
    if (this.templateState.complexInput) {
      TargetingHelper.assignComplexValuesToNewModel(this, 'keywords', 'notKeywords');
      this.init();
    } else {
      TargetingHelper.assignAllTheValuesToPipedModel(this, ADN_TARGETING_TYPES.keyword.apiProp);
      this.init();
    }
    if (matchingGroup > 0) {
      _.forEach(this.model, (m: any) => {
        m.matchingGroup = matchingGroup;
      });
    }
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.initAll();
  }
}
